import type {User} from "@project/application/admin";

import {Store, useStore} from "@tanstack/react-store";

export interface AuthState {
  /**
   * The currently authenticated user.
   * `null` when the user is not authenticated.
   *
   * Phase 1: pre-populated with a mock user to simulate a successful login.
   * Phase 2: replace with real auth provider / token exchange.
   */
  currentUser: User | null;

  /** Derived auth flag — true when currentUser is non-null. */
  isAuthenticated: boolean;

  /**
   * Simulate a successful login: store the authenticated user.
   * Phase 2: call after exchanging credentials for a session token.
   */
  login: (user: User) => void;

  /**
   * Clear the current session.
   * Phase 2: also invalidate the session token / redirect to /login.
   */
  logout: () => void;
}

/**
 * Mock current user — simulates a successful authentication response.
 * Mirrors the u1 entry in the application-layer mock store.
 * Replace with real API response data in Phase 2.
 */
const MOCK_CURRENT_USER: User = {
  bio: "Engineering lead with 8+ years of experience in full-stack development.",
  createdAt: "2024-01-10T08:00:00.000Z",
  department: "Engineering",
  email: "alice@example.com",
  id: "u1",
  image: "airbnb.png",
  isActive: true,
  lastLoginAt: "2024-03-26T14:30:00.000Z",
  loginCount: 145,
  name: "Alice Martin",
  onboardingProgress: 100,
  role: "admin",
  seniority: 5,
  taskCompletionRate: 92,
  tasks: [],
};

/**
 * TanStack Store instance for authentication state.
 * Created at module level — stable instance across renders.
 */
const authStore = new Store<AuthState>({
  currentUser: MOCK_CURRENT_USER,
  isAuthenticated: true,

  login: (user: User) => {
    authStore.setState((state) => ({
      ...state,
      currentUser: user,
      isAuthenticated: true,
    }));
  },

  logout: () => {
    authStore.setState((state) => ({
      ...state,
      currentUser: null,
      isAuthenticated: false,
    }));
  },
});

/**
 * React hook to access auth state with fine-grained subscriptions.
 * Only re-renders when the selected state slice changes.
 *
 * @example
 * const currentUser = useAuthStore((state) => state.currentUser);
 * const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
 */
export function useAuthStore<T>(selector: (state: AuthState) => T): T {
  return useStore(authStore, selector);
}
